import React from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { handleAuthFailure, isAuthFailure } from './authEvents';

/**
 * Query Key Conventions:
 *
 * All query keys follow a hierarchical pattern: [entity, scope, ...params]
 *
 * - ['machines', 'list', page?, perPage?]  — paginated machine list
 * - ['machine', id]                        — single machine detail
 * - ['templates', 'list']                  — template list
 * - ['templates', 'detail', id]            — single template detail
 * - ['templates', 'versions', templateId]  — version history for a template
 * - ['schedules']                          — schedules list
 * - ['alerts']                             — alerts list
 * - ['results', 'list']                    — inspection results list
 * - ['machineTypes', 'list']               — machine types (settings)
 * - ['machineParts', 'list']               — machine parts (settings)
 * - ['categories', 'list']                 — checklist categories (settings)
 *
 * Invalidation pattern:
 *   On every successful mutation, invalidate the parent list key so TanStack Query
 *   refetches and the list view reflects latest server state.
 *   e.g. after saving a machine → invalidateQueries({ queryKey: ['machines', 'list'] })
 */

/** Detect an auth failure from an error thrown by the generated client (ResponseError.response). */
function errorIsAuthFailure(error: unknown): boolean {
  const res = (error as { response?: Response })?.response;
  return !!res && isAuthFailure(res.status);
}

const queryClient = new QueryClient({
  // Backstop: if any query or mutation fails with a 401/403 (e.g. a raw error that didn't pass
  // through the apiConfig middleware), trigger the same logout+redirect.
  queryCache: new QueryCache({
    onError: (error) => {
      if (errorIsAuthFailure(error)) handleAuthFailure();
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (errorIsAuthFailure(error)) handleAuthFailure();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — sidebar navigation within this window serves cached data without refetch
      gcTime: 5 * 60 * 1000, // 5 minutes — keep inactive cache longer for back-navigation
      // Don't retry auth failures — retrying an expired token just delays the redirect and leaves
      // the page "loading" for seconds. Other errors keep the original 3-retry behavior.
      retry: (failureCount, error) => {
        if (errorIsAuthFailure(error)) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export { queryClient };
